import React, { useCallback } from 'react';

import { PetPartnersWelcomeCard } from './PetPartnersWelcomeCard';
import { WelcomeContent } from './WelcomeContent';
import { PolicyInformation } from './PolicyInformation';
import { CompletePetDetails } from './CompletePetDetails';
import { CompletePetRemoval } from './CompletePetRemoval';
import { Agreements } from './Agreements';
import { ReviewIneligiblePets } from './ReviewIneligiblePets';
import FigoLottie from '../common/FigoLottie';
import { SubmitFailure, SubmitSuccess } from './submit';

import { PET_DEMOS_STEPS } from './petDemographics.constants';
import { usePetDemographics, usePetDemosSteps } from './hooks';

import styles from './WelcomeScreen.module.css';

const WelcomeScreenRaw = () => {
  const {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToSpecificStep,
    initialStep,
    isCoveragePeriodCompleted,
  } = usePetDemosSteps();

  const {
    adjustedPeriodCounts,
    adjustedRemovalDates,
    coveragePeriodPetsData,
    coveragePeriodSelected,
    declarations,
    filledFromRolloverPets,
    getOriginalPetName,
    handleCancelReplacement,
    handleConfirmReplacement,
    handleReplacePetSave,
    handleSelectedPetsForRemovalChange,
    handleUndoReplace,
    handleUpdatePetDemographics,
    ineligiblePets,
    isPostSubmissionMode,
    isReplaced,
    loading,
    maxAgeByPeriod,
    onCompletePetInfoContinuePress,
    onCompletePetRemovalContinue,
    onCoveragePeriodSelected,
    onGoBackAndRetryPress,
    onReviewIneligiblePetsContinue,
    onSubmit,
    onSubmitAgreement,
    onSubmitContinuePress,
    petDemographics,
    petDemosData,
    petQuantityToRemove,
    petRemovals,
    refreshingPets,
    selectedPetsForRemoval,
    setSelectedRolloverPetsForGroup,
    setSkippedRolloverGroupId,
    submitting,
    usePendingDemographicsFF,
  } = usePetDemographics(goToSpecificStep);

  const renderStepContent = useCallback(() => {
    switch (currentStep) {
      // step 1
      case PET_DEMOS_STEPS.WELCOME:
        return <WelcomeContent goToNextStep={goToNextStep} />;

      // step 2
      case PET_DEMOS_STEPS.YOUR_POLICY:
        return (
          <PolicyInformation
            adjustedPeriodCounts={adjustedPeriodCounts}
            goToNextStep={goToNextStep}
            isCoveragePeriodCompleted={isCoveragePeriodCompleted}
            onCoveragePeriodSelected={onCoveragePeriodSelected}
            onSubmit={onSubmit}
            petsDataByCoveragePeriod={coveragePeriodPetsData}
            policyData={petDemosData}
            setSelectedRolloverPetsForGroup={setSelectedRolloverPetsForGroup}
            setSkippedRolloverGroupId={setSkippedRolloverGroupId}
            submitting={submitting}
          />
        );

      // step 3
      case PET_DEMOS_STEPS.COMPLETE_PET_DETAILS:
        return (
          <CompletePetDetails
            coveragePeriodSelected={coveragePeriodSelected}
            filledFromRolloverPets={filledFromRolloverPets}
            getOriginalPetName={getOriginalPetName}
            isPostSubmissionMode={isPostSubmissionMode}
            isReplaced={isReplaced}
            onCancelReplacement={handleCancelReplacement}
            onCompletePetInfoContinuePress={onCompletePetInfoContinuePress}
            onConfirmReplacement={handleConfirmReplacement}
            onGoBackClick={goToPreviousStep}
            onReplaceSubmit={handleReplacePetSave}
            onUndoReplace={handleUndoReplace}
            onUpdatePetDemographics={handleUpdatePetDemographics}
            petDemographics={petDemographics}
          />
        );

      // step 6 (optional)
      case PET_DEMOS_STEPS.COMPLETE_PET_REMOVAL:
        return (
          <CompletePetRemoval
            coveragePeriodSelected={coveragePeriodSelected}
            onCompletePetRemovalContinue={onCompletePetRemovalContinue}
            onGoBackClick={goToPreviousStep}
            onSelectedPetsChange={handleSelectedPetsForRemovalChange}
            petQuantityToRemove={petQuantityToRemove}
            petRemovals={petRemovals}
            removalDates={adjustedRemovalDates}
            selectedPets={selectedPetsForRemoval}
          />
        );

      // step 7 (optional)
      case PET_DEMOS_STEPS.INELIGIBLE_PETS:
        return (
          <ReviewIneligiblePets
            coveragePeriodSelected={coveragePeriodSelected}
            ineligiblePets={ineligiblePets}
            maxAgeByPeriod={maxAgeByPeriod}
            onContinueClick={onReviewIneligiblePetsContinue}
            onGoBackClick={goToPreviousStep}
          />
        );

      // step 8 (optional)
      case PET_DEMOS_STEPS.AGREEMENTS:
        return (
          <Agreements
            declarations={declarations}
            onGoBackClick={goToPreviousStep}
            onSubmit={onSubmitAgreement}
          />
        );

      case PET_DEMOS_STEPS.SUBMIT_FAILURE:
        return (
          <SubmitFailure
            onGoBackAndRetryPress={onGoBackAndRetryPress(goToPreviousStep)}
          />
        );

      case PET_DEMOS_STEPS.SUBMIT_SUCCESS:
        return (
          <SubmitSuccess
            loading={refreshingPets}
            onContinuePress={onSubmitContinuePress}
          />
        );

      default:
        return <span>WIP STEP NUMBER: {currentStep}</span>;
    }
  }, [
    adjustedPeriodCounts,
    coveragePeriodPetsData,
    coveragePeriodSelected,
    currentStep,
    goToNextStep,
    goToPreviousStep,
    handleSelectedPetsForRemovalChange,
    handleUpdatePetDemographics,
    ineligiblePets,
    onCompletePetInfoContinuePress,
    onCompletePetRemovalContinue,
    onCoveragePeriodSelected,
    onReviewIneligiblePetsContinue,
    onSubmit,
    petDemographics,
    petDemosData,
    petQuantityToRemove,
    petRemovals,
    refreshingPets,
    selectedPetsForRemoval,
    submitting,
    filledFromRolloverPets,
  ]);

  if (!usePendingDemographicsFF) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <FigoLottie height={40} width={40} />
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.mainContainer} data-initial-step={initialStep}>
        <PetPartnersWelcomeCard initialStep={initialStep}>
          {renderStepContent()}
        </PetPartnersWelcomeCard>
      </div>
    </div>
  );
};

const WelcomeScreen = React.memo(WelcomeScreenRaw);

export { WelcomeScreen };
